import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CATEGORIES, customFieldKey, DOC_STAGE_LABEL, FIELD_BY_KEY, FIELD_GROUPS, fieldLabel, isKnownField, signingOf, unsignableFields, type DocCategory } from '@pti/domain';
import { api } from '../api';
import {
  atoms, blocks, deleteRange, fieldsUsed, FIELD_IN_TEXT, hasDrawing, insertField, openDocx, paraFormat, paragraphAt, paraText, replaceAll,
  replaceWithField, restore, runFormat, saveDocx, snapshot, suggestions, type Block, type DocxModel, type Spot, type Suggestion,
} from '../lib/docx';
import { ErrorNote, Icon, Loading, Pill, toast } from '../components/ui';
import { ConfirmFlow } from '../components/Confirm';
import { TemplateDetailsFields, signerText, type TemplateInfo, type TemplateMetaInput } from './Templates';

const DRAG_TYPE = 'application/x-pti-field';

/** Upload a Word document (or open an existing template) and place PTI People fields in it. */
export function TemplatePrepare() {
  const { key } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ['templates', 'all'], queryFn: () => api.get<TemplateInfo[]>('/api/templates?archived=1'), enabled: !!key });
  const existing = templates.data?.find((t) => t.key === key);
  const [model, setModel] = useState<DocxModel | null>(null);
  const [fileName, setFileName] = useState('');
  const [loadError, setLoadError] = useState<unknown>(null);
  const [ver, setVer] = useState(0);
  const [history, setHistory] = useState<string[][]>([]);
  const [spot, setSpot] = useState<Spot | null>(null);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(false);
  const bump = () => setVer((v) => v + 1);

  // Existing template: load its Word file.
  useEffect(() => {
    if (!key || !existing || model) return;
    api.blob(`/api/templates/${key}/file`).then(async (b) => { setModel(await openDocx(b)); setFileName(existing.fileName ?? `${existing.name}.docx`); }).catch(setLoadError);
  }, [key, existing, model]);

  const load = async (f: File) => {
    setLoadError(null);
    if (!/\.docx$/i.test(f.name)) { setLoadError(new Error(/\.doc$/i.test(f.name) ? 'This is an older .doc file. Open it in Word and use File → Save As → Word Document (.docx), then upload that.' : 'Choose a Word document (.docx).')); return; }
    try { setModel(await openDocx(f)); setFileName(f.name); setHistory([]); setSpot(null); } catch (e) { setLoadError(e); }
  };

  /** Every change goes through here so it can be undone. */
  const change = useCallback((fn: (m: DocxModel) => void) => {
    if (!model) return;
    setHistory((h) => [...h.slice(-29), snapshot(model)]);
    fn(model);
    bump();
  }, [model]);

  const undo = useCallback(() => {
    if (!model || !history.length) return;
    restore(model, history[history.length - 1]!);
    setHistory((h) => h.slice(0, -1));
    setSpot(null);
    bump();
  }, [model, history]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !(e.target as HTMLElement).closest('input,textarea,select')) { e.preventDefault(); undo(); } };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [undo]);

  const insert = (fieldKey: string, at: Spot | null = spot) => {
    if (!model) return;
    if (!at) { toast('First click in the document where the field should go'); return; }
    const p = paragraphAt(model, at.part, at.pid);
    if (!p) return;
    change(() => { if (at.end > at.start) replaceWithField(p, at.start, at.end, fieldKey); else insertField(p, at.start, fieldKey); });
    const after = at.start + `{{${fieldKey}}}`.length;
    setSpot({ ...at, start: after, end: after });
    toast(`${fieldLabel(fieldKey)} added`);
  };

  const removeField = (part: string, pid: number, start: number, len: number) => {
    if (!model) return;
    const p = paragraphAt(model, part, pid);
    if (p) change(() => { deleteRange(p, start, start + len); });
    setSpot({ part, pid, start, end: start });
  };

  if (key && templates.isLoading) return <Loading />;
  if (key && templates.data && !existing) return <div className="page"><ErrorNote error={new Error('Template not found')} /></div>;
  if (key && existing && existing.kind !== 'word') return <div className="page"><ErrorNote error={new Error('Only Word templates are prepared here. Built-in templates can be replaced by uploading your own Word document.')} /></div>;

  return (
    <div className="page prep-page">
      <Link className="link back" to="/templates"><Icon n="back" s={15} /> Templates</Link>
      <div className="page-h">
        <div>
          <p className="eyebrow">{existing ? `Prepare again · version ${existing.version}` : 'New template'}</p>
          <h1>{existing ? existing.name : 'Prepare a Word document'}</h1>
          <p className="lede">Click in the document where information should go, then pick the field. Or drag a field onto the document.</p>
        </div>
        {model && (
          <div className="row gap wrap-row">
            <button className="btn" disabled={!history.length} onClick={undo} title="Undo (Ctrl+Z)">Undo</button>
            <button className="btn primary" onClick={() => setSaving(true)}>{existing ? 'Save new version' : 'Save template'}</button>
          </div>
        )}
      </div>

      {!model ? (
        key ? (loadError ? <ErrorNote error={loadError} /> : <Loading />) : (
          <div className="stack">
            <label htmlFor="tpl-file" className={`drop${drag ? ' over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) void load(f); }}>
              <Icon n="doc" s={30} />
              <strong>Drop a Word document here, or click to choose</strong>
              <span className="small dim">Your contract, letter or form as it is today (.docx). Its layout, logo, headers and footers are kept exactly.</span>
              <input id="tpl-file" type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void load(f); }} />
            </label>
            {loadError != null && <ErrorNote error={loadError} />}
            <div className="callout subtle small"><Icon n="eye" s={16} /><span>Tip: if your document already has placeholders like <strong>[Employee Name]</strong>, <strong>«Start_Date»</strong> or blank lines (<strong>Name: ______</strong>), PTI People will find them and suggest the matching field.</span></div>
          </div>
        )
      ) : (
        <div className="prep">
          <DocView model={model} ver={ver} spot={spot} setSpot={setSpot} onDropField={(k, at) => insert(k, at)} onRemoveField={removeField} />
          <aside className="prep-side">
            <InsertPanel model={model} ver={ver} spot={spot} onInsert={(k) => insert(k)} />
            <SuggestPanel model={model} ver={ver} onReplace={(s, k) => { let n = 0; change((m) => { n = replaceAll(m, s, k); }); toast(n ? `Replaced ${n === 1 ? '' : `${n} × `}${s.text.startsWith('_') ? `blank after “${s.label}”` : s.text} with ${fieldLabel(k)}` : 'That place has changed — check the document'); }} />
            <UsedPanel model={model} ver={ver} />
            <p className="small dim">{fileName}</p>
          </aside>
        </div>
      )}

      {saving && model && (
        <SaveDialog model={model} existing={existing} fileName={fileName} onClose={() => setSaving(false)}
          onSaved={(k) => { void qc.invalidateQueries({ queryKey: ['templates'] }); toast('Template saved'); nav(`/templates?saved=${k}`); }} />
      )}
    </div>
  );
}

// ───────────────────────────── The document ─────────────────────────────

function locate(container: HTMLElement, node: Node, offset: number): { part: string; pid: number; pos: number } | null {
  const el = node.nodeType === 3 ? node.parentElement : (node as Element);
  const para = el?.closest<HTMLElement>('[data-pid]');
  if (!para || !container.contains(para)) return null;
  const total = Number(para.dataset.len);
  let pos = total;
  const span = el?.closest<HTMLElement>('[data-o]');
  if (span && para.contains(span)) {
    const o = Number(span.dataset.o);
    if (span.dataset.field) pos = offset > 0 && node === span ? o + Number(span.dataset.len) : o;
    else pos = node.nodeType === 3 ? o + offset : o + (offset > 0 ? Number(span.dataset.len ?? 0) : 0);
  } else {
    const r = document.createRange();
    r.setStart(node, offset);
    const next = Array.from(para.querySelectorAll<HTMLElement>('[data-o]')).find((s) => r.comparePoint(s, 0) >= 0);
    if (next) pos = Number(next.dataset.o);
  }
  return { part: para.dataset.part!, pid: Number(para.dataset.pid), pos: Math.max(0, Math.min(total, pos)) };
}

function caretFromPoint(x: number, y: number): { node: Node; offset: number } | null {
  const d = document as Document & { caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null; caretRangeFromPoint?: (x: number, y: number) => Range | null };
  const p = d.caretPositionFromPoint?.(x, y);
  if (p) return { node: p.offsetNode, offset: p.offset };
  const r = d.caretRangeFromPoint?.(x, y);
  return r ? { node: r.startContainer, offset: r.startOffset } : null;
}

function DocView({ model, ver, spot, setSpot, onDropField, onRemoveField }: {
  model: DocxModel; ver: number; spot: Spot | null; setSpot: (s: Spot | null) => void;
  onDropField: (key: string, at: Spot) => void; onRemoveField: (part: string, pid: number, start: number, len: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [dropOver, setDropOver] = useState(false);

  // Track where the cursor is (or what is selected) in the document.
  useEffect(() => {
    const onSel = () => {
      const c = ref.current; const sel = document.getSelection();
      if (!c || !sel || sel.rangeCount === 0 || !c.contains(sel.anchorNode)) return;
      const r = sel.getRangeAt(0);
      const a = locate(c, r.startContainer, r.startOffset);
      const b = locate(c, r.endContainer, r.endOffset);
      if (!a) return;
      if (!b || b.part !== a.part || b.pid !== a.pid) { setSpot({ part: a.part, pid: a.pid, start: a.pos, end: a.pos }); return; }
      setSpot({ part: a.part, pid: a.pid, start: Math.min(a.pos, b.pos), end: Math.max(a.pos, b.pos) });
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, [setSpot]);

  const NAV_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Escape'];

  return (
    <div
      ref={ref} className={`prep-doc${dropOver ? ' over' : ''}`} contentEditable suppressContentEditableWarning spellCheck={false}
      role="textbox" aria-label="Document — click where a field should go" aria-multiline="true" aria-readonly="true"
      // The document is shown with a cursor so you can place fields, but it can't be typed into here — the wording is edited in Word.
      onBeforeInput={(e) => e.preventDefault()}
      onKeyDown={(e) => { if (!NAV_KEYS.includes(e.key) && !((e.ctrlKey || e.metaKey) && ['c', 'a', 'z'].includes(e.key.toLowerCase()))) e.preventDefault(); }}
      onPaste={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDropOver(true); } }}
      onDragLeave={() => setDropOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDropOver(false);
        const key = e.dataTransfer.getData(DRAG_TYPE);
        const c = caretFromPoint(e.clientX, e.clientY);
        const at = key && c && ref.current ? locate(ref.current, c.node, c.offset) : null;
        if (key && at) onDropField(key, { part: at.part, pid: at.pid, start: at.pos, end: at.pos });
        else if (key) toast('Drop the field onto a line of text');
      }}
    >
      {model.parts.map((part) => (
        <section key={`${part.name}-${ver}`} className={`prep-part${part.name.includes('document') ? '' : ' hf'}`}>
          {!part.name.includes('document') && <p className="prep-part-l" contentEditable={false}>{part.label}</p>}
          <Blocks list={blocks(part)} part={part.name} spot={spot} onRemoveField={onRemoveField} />
        </section>
      ))}
    </div>
  );
}

function Blocks({ list, part, spot, onRemoveField }: { list: Block[]; part: string; spot: Spot | null; onRemoveField: (part: string, pid: number, start: number, len: number) => void }) {
  return (
    <>
      {list.map((b, i) => b.type === 'p'
        ? <Para key={i} el={b.el} pid={b.pid} part={part} spot={spot?.part === part && spot.pid === b.pid ? spot : null} onRemoveField={onRemoveField} />
        : (
          <table key={i} className="prep-tbl"><tbody>{b.rows.map((row, r) => <tr key={r}>{row.map((cell, c) => <td key={c}><Blocks list={cell} part={part} spot={spot} onRemoveField={onRemoveField} /></td>)}</tr>)}</tbody></table>
        ))}
    </>
  );
}

function Para({ el, pid, part, spot, onRemoveField }: { el: Element; pid: number; part: string; spot: Spot | null; onRemoveField: (part: string, pid: number, start: number, len: number) => void }) {
  const f = paraFormat(el);
  const as = atoms(el);
  const total = as.reduce((n, a) => n + a.len, 0);
  const out: ReactNode[] = [];
  as.forEach((a, i) => {
    const rf = runFormat(a.run);
    const style = { fontWeight: rf.bold ? 700 : undefined, fontStyle: rf.italic ? 'italic' : undefined, textDecoration: rf.underline ? 'underline' : undefined, fontSize: rf.sizePt ? `${Math.min(Math.max(rf.sizePt, 7), 28)}pt` : undefined, textTransform: rf.caps ? 'uppercase' as const : undefined, color: rf.color };
    if (a.kind === 'tab') { out.push(<span key={i} data-o={a.start} data-len={1} className="prep-tab">{'\t'}</span>); return; }
    if (a.kind === 'br') { out.push(<span key={i} data-o={a.start} data-len={1}>{'\n'}</span>); return; }
    let last = 0;
    for (const m of a.text.matchAll(FIELD_IN_TEXT)) {
      const s = m.index!;
      if (s > last) out.push(<span key={`${i}-${last}`} data-o={a.start + last} data-len={s - last} style={style}>{a.text.slice(last, s)}</span>);
      const k = m[1]!; const o = a.start + s; const len = m[0].length;
      out.push(
        <span key={`${i}-f${s}`} className={`fld${signingOf(k) ? ' sig' : isKnownField(k) ? '' : ' typed'}`} data-o={o} data-len={len} data-field={k} contentEditable={false} title={`${fieldLabel(k)} — {{${k}}}`}>
          {fieldLabel(k)}
          <button type="button" className="fld-x" aria-label={`Remove ${fieldLabel(k)}`} onMouseDown={(e) => e.preventDefault()} onClick={() => onRemoveField(part, pid, o, len)}>×</button>
        </span>,
      );
      last = s + len;
    }
    if (last < a.text.length) out.push(<span key={`${i}-${last}`} data-o={a.start + last} data-len={a.text.length - last} style={style}>{a.text.slice(last)}</span>);
  });
  const Tag = f.heading ? (`h${f.heading + 1}` as 'h2') : 'p';
  return (
    <Tag className={`dp${spot ? ' here' : ''}`} data-part={part} data-pid={pid} data-len={total} style={{ textAlign: f.align, marginLeft: f.indentPt ? `${f.indentPt}pt` : undefined }}>
      {f.list && <span className="dp-bullet" contentEditable={false}>•</span>}
      {hasDrawing(el) && <span className="dp-pic" contentEditable={false}>picture</span>}
      {out.length ? out : <br />}
    </Tag>
  );
}

// ───────────────────────────── Side panels ─────────────────────────────

function context(model: DocxModel, spot: Spot | null): ReactNode {
  if (!spot) return <span className="dim">Click in the document to choose where the field goes.</span>;
  const p = paragraphAt(model, spot.part, spot.pid);
  if (!p) return null;
  const t = paraText(p).replace(/\t/g, ' ').replace(/\n/g, ' ');
  const show = (s: string) => s.replace(FIELD_IN_TEXT, (_m, k: string) => `[${fieldLabel(k)}]`);
  const before = show(t.slice(Math.max(0, spot.start - 40), spot.start));
  const sel = t.slice(spot.start, spot.end);
  const after = show(t.slice(spot.end, spot.end + 30));
  const where = model.parts.find((x) => x.name === spot.part)?.label;
  return (
    <span className="ctx">
      {where !== 'Document' && <span className="dim">{where}: </span>}
      {spot.start > 40 && '…'}{before}
      {sel ? <mark title="This text will be replaced by the field">{sel}</mark> : <span className="caret" aria-label="cursor" />}
      {after}{spot.end + 30 < t.length && '…'}
      {!t && <span className="dim"> (empty line)</span>}
    </span>
  );
}

function InsertPanel({ model, ver, spot, onInsert }: { model: DocxModel; ver: number; spot: Spot | null; onInsert: (key: string) => void }) {
  const [q, setQ] = useState('');
  const [custom, setCustom] = useState('');
  const groups = useMemo(() => FIELD_GROUPS.map((g) => ({ ...g, fields: g.fields.filter((f) => !q || `${f.label} ${g.label} ${f.example}`.toLowerCase().includes(q.toLowerCase())) })).filter((g) => g.fields.length), [q]);
  void ver;
  const customKey = customFieldKey(custom);
  return (
    <div className="prep-box">
      <h3>Add a field</h3>
      <div className="prep-ctx small"><strong>{spot && spot.end > spot.start ? 'Replace:' : 'Insert at:'}</strong> {context(model, spot)}</div>
      <input className="txt" type="search" placeholder="Search fields — name, address, pay…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search fields" />
      <div className="fld-list">
        {groups.map((g) => (
          <div key={g.id}>
            <h4>{g.label}</h4>
            {g.fields.map((f) => (
              <button key={f.key} type="button" className="fld-pick" draggable
                onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, f.key); e.dataTransfer.setData('text/plain', `{{${f.key}}}`); e.dataTransfer.effectAllowed = 'copy'; }}
                onClick={() => onInsert(f.key)} title="Click to add at the cursor, or drag onto the document">
                <span className="grip" aria-hidden="true">⋮⋮</span>
                <span><strong>{f.label}</strong>{f.restricted && <Pill tone="warn">Director / RTOM</Pill>}<span className="small dim block">{f.example}</span></span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="stack-s">
        <h4>Your own field</h4>
        <p className="small dim">For anything PTI People doesn’t hold — e.g. “Probation period”. It’s typed in each time a document is made.</p>
        <div className="row gap-s">
          <input className="txt" placeholder="Field name" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="Your own field name" />
          <button type="button" className="btn" disabled={!customKey} onMouseDown={(e) => e.preventDefault()} onClick={() => { onInsert(customKey); setCustom(''); }}>Add</button>
        </div>
      </div>
    </div>
  );
}

function SuggestPanel({ model, ver, onReplace }: { model: DocxModel; ver: number; onReplace: (s: Suggestion, key: string) => void }) {
  const found = useMemo(() => suggestions(model), [model, ver]);
  const [pick, setPick] = useState<Record<string, string>>({});
  if (!found.length) return null;
  const id = (s: Suggestion) => s.id;
  return (
    <div className="prep-box">
      <h3>Found in your document</h3>
      <p className="small dim">Placeholders already in the document. Check the match, then replace.</p>
      <ul className="sugg">
        {found.map((s) => {
          const k = pick[id(s)] ?? s.guess ?? '';
          return (
            <li key={id(s)}>
              <span className="sugg-t"><code>{s.blank ? `${s.label}: ____` : s.text}</code>{s.count > 1 && <span className="small dim"> × {s.count}</span>}{s.context && <span className="small dim"> under “{s.context}”</span>}</span>
              <div className="row gap-s">
                <select aria-label={`Field for ${s.text}`} value={k} onChange={(e) => setPick({ ...pick, [id(s)]: e.target.value })}>
                  <option value="">Choose a field…</option>
                  {FIELD_GROUPS.map((g) => <optgroup key={g.id} label={g.label}>{g.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}</optgroup>)}
                  <optgroup label="Your own field"><option value={customFieldKey(s.label)}>“{s.label}” (typed in when sending)</option></optgroup>
                </select>
                <button type="button" className="btn sm" disabled={!k} onClick={() => onReplace(s, k)}>Replace{s.count > 1 ? ' all' : ''}</button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function UsedPanel({ model, ver }: { model: DocxModel; ver: number }) {
  const used = useMemo(() => fieldsUsed(model), [model, ver]);
  return (
    <div className="prep-box">
      <h3>Fields in this document <span className="dim">({used.length})</span></h3>
      {!used.length ? <p className="small dim">None yet.</p> : (
        <ul className="plain-list small">
          {used.map((u) => (
            <li key={u.key}>
              <span><strong>{fieldLabel(u.key)}</strong>{u.count > 1 && <span className="dim"> × {u.count}</span>}</span>
              {signingOf(u.key) ? <Pill tone="ok">when signed</Pill> : isKnownField(u.key) ? (FIELD_BY_KEY[u.key]?.restricted ? <Pill tone="warn">Pay</Pill> : <span className="dim">from records</span>) : <Pill tone="accent">typed in</Pill>}
            </li>
          ))}
        </ul>
      )}
      {!used.some((u) => signingOf(u.key)) && <p className="small dim">Tip: put <strong>Signature</strong> and <strong>Date signed</strong> fields on the signature lines so each person signs in the right place. Without them, signatures go on a page added at the end.</p>}
      <p className="small dim">To remove a field, click × on it in the document.</p>
    </div>
  );
}

// ───────────────────────────── Saving ─────────────────────────────

function SaveDialog({ model, existing, fileName, onClose, onSaved }: { model: DocxModel; existing?: TemplateInfo; fileName: string; onClose: () => void; onSaved: (key: string) => void }) {
  const [meta, setMeta] = useState<TemplateMetaInput>({
    name: existing?.name ?? fileName.replace(/\.docx$/i, '').replace(/[_-]+/g, ' '),
    category: (existing?.category as DocCategory) ?? 'contract', stage: existing?.stage ?? 'offer', description: existing?.description ?? '',
    signers: existing?.signers ?? ['person', 'director'],
  });
  const used = fieldsUsed(model);
  const typed = used.filter((u) => !isKnownField(u.key) || u.key === 'offer.conditions');
  const signPlaces = used.filter((u) => signingOf(u.key));
  const SIGNER: Record<string, string> = { person: 'the person', director: 'the Director', rtom: 'the RTO Manager' };
  return (
    <ConfirmFlow<{ key: string }>
      title={existing ? `Save a new version of ${existing.name}` : 'Save the template'}
      onClose={onClose}
      canContinue={meta.name.trim().length >= 2}
      review={() => {
        const orphan = unsignableFields(used.map((u) => u.key), meta.signers);
        if (orphan.length) throw new Error(`The document has ${orphan.map(fieldLabel).join(', ')}, but that person isn’t in “Who signs”. Add them below, or remove the field from the document.`);
        return (
        <>
          <p>{existing ? 'This replaces the template for documents made from now on. Documents already made or signed are not changed.' : 'This adds the template so it can be used to make documents.'}</p>
          <ul className="plain-list">
            <li><strong>Name:</strong> {meta.name}</li>
            <li><strong>Where it fits:</strong> {meta.stage ? DOC_STAGE_LABEL[meta.stage] : 'Other'} · {CATEGORIES[meta.category].label}</li>
            <li><strong>Signed by:</strong> {signerText(meta.signers)}</li>
            <li><strong>Fields:</strong> {used.filter((u) => !signingOf(u.key)).map((u) => fieldLabel(u.key)).join(', ') || 'none'}</li>
            <li><strong>Signs on the document:</strong> {signPlaces.length ? [...new Set(signPlaces.map((u) => SIGNER[signingOf(u.key)!.signer]))].join(', ') : 'no signature places — signatures go on a page added at the end'}</li>
          </ul>
          {meta.signers.filter((s) => !signPlaces.some((u) => signingOf(u.key)!.signer === s && signingOf(u.key)!.kind === 'sign')).length > 0 && signPlaces.length > 0 && (
            <p className="small">No signature place for {meta.signers.filter((s) => !signPlaces.some((u) => signingOf(u.key)!.signer === s && signingOf(u.key)!.kind === 'sign')).map((s) => SIGNER[s]).join(', ')} — their signature is recorded on the certificate page only.</p>
          )}
          {typed.length > 0 && <p className="small">Typed in each time a document is made: {typed.map((u) => fieldLabel(u.key)).join(', ')}.</p>}
          {used.some((u) => FIELD_BY_KEY[u.key]?.restricted) && <p className="small">This template includes pay, so only the Director or RTO Manager can make documents from it.</p>}
          {!used.length && <p className="small tone-warn">There are no fields in this document yet — every copy will be identical.</p>}
        </>
        );
      }}
      confirmLabel={existing ? 'Yes, save the new version' : 'Yes, save the template'}
      onConfirm={async () => {
        const blob = await saveDocx(model);
        const fd = new FormData();
        fd.append('meta', JSON.stringify({ ...meta, description: meta.description?.trim() || null }));
        fd.append('file', new File([blob], fileName.toLowerCase().endsWith('.docx') ? fileName : `${fileName}.docx`, { type: blob.type }));
        return api.upload<{ key: string }>(existing ? `/api/templates/${existing.key}` : '/api/templates', fd);
      }}
      onDone={(r) => onSaved(r.key)}
    >
      <TemplateDetailsFields value={meta} onChange={setMeta} />
    </ConfirmFlow>
  );
}
